import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Filter,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getActivityLogs, getWorkspaceActivityLogs } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';

const ACTION_LABELS = {
  'menu.create': 'Menu Created',
  'menu.delete': 'Menu Deleted',
  'menu.publish': 'Menu Published',
  'menu.set_default': 'Menu set as default',
  'menu.unset_default': 'Menu default removed',
  'alias.create': 'Alias Created',
  'alias.update': 'Alias Updated',
  'alias.delete': 'Alias Deleted',
  'channel.create': 'Channel Created',
  'channel.delete': 'Channel Deleted',
};

const ACTION_COLORS = {
  'menu.create': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'menu.delete': 'bg-red-500/10 text-red-400 border-red-500/20',
  'menu.publish': 'bg-green-500/10 text-green-400 border-green-500/20',
  'menu.set_default': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'menu.unset_default': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  'alias.create': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'alias.update': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'alias.delete': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  'channel.create': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  'channel.delete': 'bg-red-500/10 text-red-400 border-red-500/20',
};

function formatTimestamp(ts) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    }).format(new Date(ts));
  } catch {
    return ts;
  }
}

function LogRow({ log }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = log.details && Object.keys(log.details).length > 0;

  return (
    <div className="border border-[#27272A] rounded-lg overflow-hidden">
      <div
        className="flex items-start gap-3 p-3 sm:p-4 cursor-pointer hover:bg-[#18181B] transition-colors"
        onClick={() => hasDetails && setExpanded(e => !e)}
      >
        {/* Status icon */}
        <div className="flex-shrink-0 mt-0.5">
          {log.status === 'success'
            ? <CheckCircle2 className="w-4 h-4 text-[#06C755]" />
            : <XCircle className="w-4 h-4 text-red-400" />
          }
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`text-xs font-mono px-2 py-0.5 rounded border ${ACTION_COLORS[log.action] || 'bg-[#27272A] text-[#A1A1AA] border-[#27272A]'}`}>
              {ACTION_LABELS[log.action] || log.action}
            </span>
            <span className="text-sm font-medium text-[#FAFAFA] truncate">
              {log.resourceName || log.resourceId}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[#71717A]">
            <span>{formatTimestamp(log.timestamp)}</span>
            <span>{log.userName} ({log.userEmail})</span>
            {log.error && (
              <span className="text-red-400 font-mono truncate max-w-xs">{log.error}</span>
            )}
          </div>
        </div>

        {/* Expand toggle */}
        {hasDetails && (
          <div className="flex-shrink-0 text-[#52525B]">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        )}
      </div>

      {/* Expanded details */}
      {expanded && hasDetails && (
        <div className="border-t border-[#27272A] bg-[#0a0a0b] p-4">
          <pre className="text-xs text-[#A1A1AA] font-mono whitespace-pre-wrap break-all overflow-auto max-h-64">
            {JSON.stringify(log.details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export function ActivityLogs() {
  const { isSuperAdmin } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (filterAction) params.action = filterAction;
      if (filterStatus) params.status = filterStatus;
      let data;
      if (isSuperAdmin) {
        data = await getActivityLogs(params);
      } else {
        if (!selectedWorkspaceId) return;
        data = await getWorkspaceActivityLogs(selectedWorkspaceId, params);
      }
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [offset, filterAction, filterStatus, isSuperAdmin, selectedWorkspaceId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to page 0 when filters change
  const handleFilterChange = (setter) => (val) => {
    setOffset(0);
    setter(val === 'all' ? '' : val);
  };

  const clearFilters = () => {
    setOffset(0);
    setFilterAction('');
    setFilterStatus('');
  };

  const hasFilters = filterAction || filterStatus;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold">Activity Logs</h1>
          <p className="text-sm text-[#71717A] mt-0.5">
            {isSuperAdmin
              ? `Audit trail for all system actions · ${total} total entries`
              : `${selectedWorkspace?.name || 'Workspace'} audit trail · ${total} total entries`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchLogs}
          disabled={loading}
          className="border-[#27272A] gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-[#52525B] flex-shrink-0" />
        <Select value={filterAction || 'all'} onValueChange={handleFilterChange(setFilterAction)}>
          <SelectTrigger className="w-44 h-8 bg-[#18181B] border-[#27272A] text-sm">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent className="bg-[#18181B] border-[#27272A]">
            <SelectItem value="all" className="focus:bg-[#27272A]">All actions</SelectItem>
            {Object.entries(ACTION_LABELS).map(([val, label]) => (
              <SelectItem key={val} value={val} className="focus:bg-[#27272A]">{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterStatus || 'all'} onValueChange={handleFilterChange(setFilterStatus)}>
          <SelectTrigger className="w-36 h-8 bg-[#18181B] border-[#27272A] text-sm">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent className="bg-[#18181B] border-[#27272A]">
            <SelectItem value="all" className="focus:bg-[#27272A]">All statuses</SelectItem>
            <SelectItem value="success" className="focus:bg-[#27272A]">Success</SelectItem>
            <SelectItem value="error" className="focus:bg-[#27272A]">Error</SelectItem>
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 gap-1 text-[#71717A] hover:text-white">
            <X className="w-3 h-3" />
            Clear
          </Button>
        )}
      </div>

      {/* Log list */}
      {loading && logs.length === 0 ? (
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-16 rounded-lg bg-[#18181B] animate-pulse" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-[#52525B]">
          <p className="text-sm">No activity logs found</p>
          {hasFilters && <p className="text-xs mt-1">Try clearing the filters</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map(log => <LogRow key={log.id} log={log} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-[#71717A]">
            Page {currentPage} of {totalPages} · {total} entries
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(o => Math.max(0, o - limit))}
              className="border-[#27272A]"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + limit >= total || loading}
              onClick={() => setOffset(o => o + limit)}
              className="border-[#27272A]"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLogs;
